// src/store/slices/panchayathMasterSlice.js

import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import api from "../../services/AxiosInstance";

// ============================================================
// HELPERS
// ============================================================

const getErrorMessage = (
  error,
  fallback = "Something went wrong"
) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallback
  );
};

// ============================================================
// DISTRICTS
//
// OLD API
// GET /api/panchayath/districts
// ============================================================

export const fetchDistrictsThunk =
  createAsyncThunk(
    "panchayathMaster/fetchDistricts",

    async (_, { rejectWithValue }) => {
      try {
        const res = await api.get(
          "/api/panchayath/districts"
        );

        return (
          res.data?.rows ||
          res.data?.districts ||
          []
        );
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load districts"
          )
        );
      }
    }
  );

// ============================================================
// TALUKS
//
// OLD API
// GET /api/panchayath/taluks?dist_id=
// ============================================================

export const fetchTaluksByDistrictThunk =
  createAsyncThunk(
    "panchayathMaster/fetchTaluks",

    async (
      dist_id,
      { rejectWithValue }
    ) => {
      try {
        if (!dist_id) {
          return [];
        }

        const res = await api.get(
          "/api/panchayath/taluks",
          {
            params: {
              dist_id,
            },
          }
        );

        return (
          res.data?.rows ||
          res.data?.taluks ||
          []
        );
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load taluks"
          )
        );
      }
    }
  );

// ============================================================
// PANCHAYATH LIST
//
// OLD API
// GET /api/panchayath/panchayaths
// ============================================================

export const fetchPanchayathsThunk =
  createAsyncThunk(
    "panchayathMaster/fetchPanchayaths",

    async (
      _,
      {
        getState,
        rejectWithValue,
      }
    ) => {
      try {
        const state =
          getState();

        const filters =
          state?.panchayathMaster
            ?.filters || {};

        const res = await api.get(
          "/api/panchayath/panchayaths",
          {
            params: {
              dist_id:
                filters.dist_id ||
                "",

              taluk_id:
                filters.taluk_id ||
                "",
            },
          }
        );

        return (
          res.data?.rows ||
          res.data?.panchayaths ||
          []
        );
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load panchayaths"
          )
        );
      }
    }
  );

// ============================================================
// GET PANCHAYATH COMPLETE DETAILS
//
// NEW V1 API
// GET /api/v1/panchayath/panchayaths/:pan_id
//
// Returns:
// panchayath_master
// +
// panchayath_additional_details
// ============================================================

export const fetchPanchayathByIdThunk =
  createAsyncThunk(
    "panchayathMaster/fetchById",

    async (
      pan_id,
      { rejectWithValue }
    ) => {
      try {
        if (!pan_id) {
          return rejectWithValue(
            "pan_id required"
          );
        }

        const res = await api.get(
          `/api/v1/panchayath/panchayaths/${pan_id}`
        );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to load Panchayath"
          );
        }

        return (
          res.data?.data ||
          null
        );
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load Panchayath"
          )
        );
      }
    }
  );


// ============================================================
// GET PANCHAYATH FULL PROFILE
//
// GET
// /api/v1/panchayath/panchayaths/:pan_id/profile
// ============================================================

export const fetchPanchayathProfileThunk =
  createAsyncThunk(
    "panchayathMaster/fetchProfile",

    async (
      pan_id,
      { rejectWithValue }
    ) => {
      try {
        if (!pan_id) {
          return rejectWithValue(
            "pan_id required"
          );
        }

        const res =
          await api.get(
            `/api/v1/panchayath/panchayaths/${pan_id}/profile`
          );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to load Panchayath profile"
          );
        }

        return (
          res.data?.data ||
          null
        );
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load Panchayath profile"
          )
        );
      }
    }
  );

// ============================================================
// CREATE PANCHAYATH COMPLETE
//
// NEW V1 API
// POST /api/v1/panchayath/panchayaths
//
// One payload contains:
// basic details
// +
// contact details
// +
// billing details
// +
// sales details
// ============================================================

export const createPanchayathThunk =
  createAsyncThunk(
    "panchayathMaster/create",

    async (
      payload,
      {
        dispatch,
        rejectWithValue,
      }
    ) => {
      try {
        const res = await api.post(
          "/api/v1/panchayath/panchayaths",
          payload
        );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to create Panchayath"
          );
        }

        const pan_id =
          Number(
            res.data?.data
              ?.pan_id
          ) || null;

        // Refresh old list after create
        dispatch(
          fetchPanchayathsThunk()
        );

        return {
          success: true,

          pan_id,

          data:
            res.data?.data ||
            null,

          message:
            res.data?.message ||
            "Panchayath created successfully",
        };
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to create Panchayath"
          )
        );
      }
    }
  );

// ============================================================
// UPDATE PANCHAYATH COMPLETE
//
// NEW V1 API
// PUT /api/v1/panchayath/panchayaths/:pan_id
//
// Updates:
// panchayath_master
// +
// panchayath_additional_details
// ============================================================

export const updatePanchayathThunk =
  createAsyncThunk(
    "panchayathMaster/update",

    async (
      payload,
      {
        dispatch,
        rejectWithValue,
      }
    ) => {
      try {
        const pan_id =
          payload?.pan_id;

        if (!pan_id) {
          return rejectWithValue(
            "pan_id missing"
          );
        }

        const res = await api.put(
          `/api/v1/panchayath/panchayaths/${pan_id}`,
          payload
        );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to update Panchayath"
          );
        }

        // Refresh list
        dispatch(
          fetchPanchayathsThunk()
        );

        return {
          success: true,

          pan_id:
            Number(pan_id),

          data:
            res.data?.data ||
            null,

          message:
            res.data?.message ||
            "Panchayath updated successfully",
        };
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to update Panchayath"
          )
        );
      }
    }
  );

// ============================================================
// DELETE PANCHAYATH
//
// OLD API
// DELETE /api/panchayath/panchayaths/:pan_id
//
// Keeping old API unchanged.
// ============================================================

export const deletePanchayathThunk =
  createAsyncThunk(
    "panchayathMaster/delete",

    async (
      pan_id,
      {
        dispatch,
        rejectWithValue,
      }
    ) => {
      try {
        if (!pan_id) {
          return rejectWithValue(
            "pan_id required"
          );
        }

        const res =
          await api.delete(
            `/api/panchayath/panchayaths/${pan_id}`
          );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Delete failed"
          );
        }

        dispatch(
          fetchPanchayathsThunk()
        );

        return {
          success: true,

          pan_id:
            Number(pan_id),

          message:
            res.data?.message ||
            "Panchayath deleted successfully",
        };
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to delete Panchayath"
          )
        );
      }
    }
  );

// ============================================================
// SALES EXECUTIVE LOOKUP
//
// NEW V1 MASTER
// GET /api/v1/sales-executives/lookup
// ============================================================

export const fetchSalesExecutiveLookupThunk =
  createAsyncThunk(
    "panchayathMaster/fetchSalesExecutives",

    async (
      filters = {},
      { rejectWithValue }
    ) => {
      try {
        const res = await api.get(
          "/api/v1/sales-executives/lookup",
          {
            params: {
              search:
                filters.search ||
                "",

              status:
                filters.status ||
                "Active",
            },
          }
        );

        if (
          res.data?.success ===
          false
        ) {
          return rejectWithValue(
            res.data?.message ||
              "Failed to load Sales Executives"
          );
        }

        return (
          res.data?.data ||
          []
        );
      } catch (error) {
        return rejectWithValue(
          getErrorMessage(
            error,
            "Failed to load Sales Executives"
          )
        );
      }
    }
  );

// ============================================================
// FORM DEFAULT
// ============================================================

const EMPTY_FORM = {
  // ==========================================================
  // BASIC
  // ==========================================================

  pan_id: "",

  dist_id: "",

  taluk_id: "",

  pan_name: "",

  db_name: "",

  tenant_domain: "",

  status: 1,

  // ==========================================================
  // CONTACT DETAILS
  // ==========================================================

  pdo_name: "",

  pdo_mobile_no: "",

  deo_name: "",

  deo_mobile_no: "",

  president_name: "",

  president_mobile_no: "",

  key_person_name: "",

  key_person_mobile_no: "",

  panchayath_email_id: "",

  // ==========================================================
  // SUBSCRIPTION / BILLING
  // ==========================================================

  subscription_type: "",

  subscription_amount: "",

  tax_type: "",

  bill_generation_date: "",

  installation_amount: "",

  training_charges: "",

  printer_charges: "",

  // ==========================================================
  // SALES
  // ==========================================================

  registration_date: "",

  bill_start_date: "",

  sales_executive_id: "",

  support_executive_id: "",
};

// ============================================================
// INITIAL STATE
// ============================================================

const initialState = {
  // ==========================================================
  // MASTER DATA
  // ==========================================================

  districts: [],

  taluks: [],

  salesExecutives: [],

  // ==========================================================
  // LIST
  // ==========================================================

  rows: [],

  filters: {
    dist_id: "",
    taluk_id: "",
  },

  // ==========================================================
  // SELECTED PANCHAYATH
  // ==========================================================

  selectedPanchayath:
    null,

  // ==========================================================
  // PANCHAYATH FULL PROFILE
  // ==========================================================

  panchayathProfile:
    null,

  // ==========================================================
  // FORM
  // ==========================================================

  form: {
    ...EMPTY_FORM,
  },

  // ==========================================================
  // LOADERS
  // ==========================================================

  loading: false,

  detailsLoading: false,

  profileLoading: false,

  salesExecutiveLoading:
    false,

  saving: false,

  deleting: false,

  // ==========================================================
  // ERROR
  // ==========================================================

  error: null,
};

// ============================================================
// SLICE
// ============================================================

const slice = createSlice({
  name: "panchayathMaster",

  initialState,

  reducers: {
    // ========================================================
    // FILTER
    // ========================================================

    setPmFilters(
      state,
      action
    ) {
      state.filters = {
        ...state.filters,

        ...(action.payload ||
          {}),
      };
    },

    // ========================================================
    // TALUK
    // ========================================================

    clearTaluks(state) {
      state.taluks = [];
    },

    // ========================================================
    // FORM FIELD UPDATE
    // ========================================================

    setPmForm(
      state,
      action
    ) {
      state.form = {
        ...state.form,

        ...(action.payload ||
          {}),
      };
    },

    // ========================================================
    // RESET FORM
    // ========================================================

    resetPmForm(state) {
      state.form = {
        ...EMPTY_FORM,
      };

      state.selectedPanchayath =
        null;

      state.error = null;
    },

    // ========================================================
    // CLEAR SELECTED PANCHAYATH
    // ========================================================

    clearSelectedPanchayath(
      state
    ) {
      state.selectedPanchayath =
        null;

      state.panchayathProfile =
        null;

      state.form = {
        ...EMPTY_FORM,
      };
    },

    // ========================================================
    // CLEAR ERROR
    // ========================================================

    clearPanchayathError(
      state
    ) {
      state.error = null;
    },
  },

  // ==========================================================
  // EXTRA REDUCERS
  // ==========================================================

  extraReducers: (
    builder
  ) => {
    // ========================================================
    // DISTRICTS
    // ========================================================

    builder
      .addCase(
        fetchDistrictsThunk.pending,
        (state) => {
          state.error = null;
        }
      )

      .addCase(
        fetchDistrictsThunk.fulfilled,
        (
          state,
          action
        ) => {
          state.districts =
            action.payload ||
            [];
        }
      )

      .addCase(
        fetchDistrictsThunk.rejected,
        (
          state,
          action
        ) => {
          state.districts =
            [];

          state.error =
            action.payload ||
            "Failed to load districts";
        }
      );

    // ========================================================
    // TALUKS
    // ========================================================

    builder
      .addCase(
        fetchTaluksByDistrictThunk.pending,
        (state) => {
          state.error = null;
        }
      )

      .addCase(
        fetchTaluksByDistrictThunk.fulfilled,
        (
          state,
          action
        ) => {
          state.taluks =
            action.payload ||
            [];
        }
      )

      .addCase(
        fetchTaluksByDistrictThunk.rejected,
        (
          state,
          action
        ) => {
          state.taluks = [];

          state.error =
            action.payload ||
            "Failed to load taluks";
        }
      );

    // ========================================================
    // PANCHAYATH LIST
    // ========================================================

    builder
      .addCase(
        fetchPanchayathsThunk.pending,
        (state) => {
          state.loading =
            true;

          state.error =
            null;
        }
      )

      .addCase(
        fetchPanchayathsThunk.fulfilled,
        (
          state,
          action
        ) => {
          state.loading =
            false;

          state.rows =
            action.payload ||
            [];
        }
      )

      .addCase(
        fetchPanchayathsThunk.rejected,
        (
          state,
          action
        ) => {
          state.loading =
            false;

          state.rows = [];

          state.error =
            action.payload ||
            "Failed to load panchayaths";
        }
      );

    // ========================================================
    // GET COMPLETE PANCHAYATH BY ID
    // ========================================================

    builder
      .addCase(
        fetchPanchayathByIdThunk.pending,
        (state) => {
          state.detailsLoading =
            true;

          state.error =
            null;
        }
      )

      .addCase(
        fetchPanchayathByIdThunk.fulfilled,
        (
          state,
          action
        ) => {
          state.detailsLoading =
            false;

          const row =
            action.payload ||
            {};

          state.selectedPanchayath =
            row;

          state.form = {
            ...EMPTY_FORM,

            // ================================================
            // BASIC
            // ================================================

            pan_id:
              row.pan_id ||
              "",

            dist_id:
              row.dist_id ||
              "",

            taluk_id:
              row.taluk_id ||
              "",

            pan_name:
              row.pan_name ||
              "",

            db_name:
              row.db_name ||
              "",

            tenant_domain:
              row.tenant_domain ||
              "",

            status:
              Number(
                row.status
              ) === 0
                ? 0
                : 1,

            // ================================================
            // CONTACT DETAILS
            // ================================================

            pdo_name:
              row.pdo_name ||
              "",

            pdo_mobile_no:
              row.pdo_mobile_no ||
              "",

            deo_name:
              row.deo_name ||
              "",

            deo_mobile_no:
              row.deo_mobile_no ||
              "",

            president_name:
              row.president_name ||
              "",

            president_mobile_no:
              row.president_mobile_no ||
              "",

            key_person_name:
              row.key_person_name ||
              "",

            key_person_mobile_no:
              row.key_person_mobile_no ||
              "",

            panchayath_email_id:
              row.panchayath_email_id ||
              "",

            // ================================================
            // BILLING
            // ================================================

            subscription_type:
              row.subscription_type ||
              "",

            subscription_amount:
              row.subscription_amount ??
              "",

            tax_type:
              row.tax_type ||
              "",

            bill_generation_date:
              row.bill_generation_date
                ? String(
                    row.bill_generation_date
                  ).slice(
                    0,
                    10
                  )
                : "",

            installation_amount:
              row.installation_amount ??
              "",

            training_charges:
              row.training_charges ??
              "",

            printer_charges:
              row.printer_charges ??
              "",

            // ================================================
            // SALES
            // ================================================

            registration_date:
              row.registration_date
                ? String(
                    row.registration_date
                  ).slice(
                    0,
                    10
                  )
                : "",

            bill_start_date:
              row.bill_start_date
                ? String(
                    row.bill_start_date
                  ).slice(
                    0,
                    10
                  )
                : "",

            sales_executive_id:
              row.sales_executive_id ||
              "",

            support_executive_id:
              row.support_executive_id ||
              "",
          };
        }
      )

      .addCase(
        fetchPanchayathByIdThunk.rejected,
        (
          state,
          action
        ) => {
          state.detailsLoading =
            false;

          state.selectedPanchayath =
            null;

          state.error =
            action.payload ||
            "Failed to load Panchayath";
        }
      );


    // ========================================================
    // PANCHAYATH FULL PROFILE
    // ========================================================

    builder
      .addCase(
        fetchPanchayathProfileThunk.pending,
        (state) => {
          state.profileLoading =
            true;

          state.error =
            null;
        }
      )

      .addCase(
        fetchPanchayathProfileThunk.fulfilled,
        (
          state,
          action
        ) => {
          state.profileLoading =
            false;

          state.panchayathProfile =
            action.payload ||
            null;
        }
      )

      .addCase(
        fetchPanchayathProfileThunk.rejected,
        (
          state,
          action
        ) => {
          state.profileLoading =
            false;

          state.panchayathProfile =
            null;

          state.error =
            action.payload ||
            "Failed to load Panchayath profile";
        }
      );

    // ========================================================
    // SALES EXECUTIVE LOOKUP
    // ========================================================

    builder
      .addCase(
        fetchSalesExecutiveLookupThunk.pending,
        (state) => {
          state.salesExecutiveLoading =
            true;
        }
      )

      .addCase(
        fetchSalesExecutiveLookupThunk.fulfilled,
        (
          state,
          action
        ) => {
          state.salesExecutiveLoading =
            false;

          state.salesExecutives =
            action.payload ||
            [];
        }
      )

      .addCase(
        fetchSalesExecutiveLookupThunk.rejected,
        (
          state,
          action
        ) => {
          state.salesExecutiveLoading =
            false;

          state.salesExecutives =
            [];

          state.error =
            action.payload ||
            "Failed to load Sales Executives";
        }
      );

    // ========================================================
    // CREATE PANCHAYATH
    // ========================================================

    builder
      .addCase(
        createPanchayathThunk.pending,
        (state) => {
          state.saving =
            true;

          state.error =
            null;
        }
      )

      .addCase(
        createPanchayathThunk.fulfilled,
        (
          state,
          action
        ) => {
          state.saving =
            false;

          const pan_id =
            action.payload
              ?.pan_id;

          if (pan_id) {
            state.form.pan_id =
              pan_id;
          }
        }
      )

      .addCase(
        createPanchayathThunk.rejected,
        (
          state,
          action
        ) => {
          state.saving =
            false;

          state.error =
            action.payload ||
            "Failed to create Panchayath";
        }
      );

    // ========================================================
    // UPDATE PANCHAYATH
    // ========================================================

    builder
      .addCase(
        updatePanchayathThunk.pending,
        (state) => {
          state.saving =
            true;

          state.error =
            null;
        }
      )

      .addCase(
        updatePanchayathThunk.fulfilled,
        (state) => {
          state.saving =
            false;
        }
      )

      .addCase(
        updatePanchayathThunk.rejected,
        (
          state,
          action
        ) => {
          state.saving =
            false;

          state.error =
            action.payload ||
            "Failed to update Panchayath";
        }
      );

    // ========================================================
    // DELETE PANCHAYATH
    // ========================================================

    builder
      .addCase(
        deletePanchayathThunk.pending,
        (state) => {
          state.deleting =
            true;

          state.error =
            null;
        }
      )

      .addCase(
        deletePanchayathThunk.fulfilled,
        (
          state,
          action
        ) => {
          state.deleting =
            false;

          const deletedId =
            Number(
              action.payload
                ?.pan_id
            );

          if (deletedId) {
            state.rows =
              state.rows.filter(
                (row) =>
                  Number(
                    row.pan_id
                  ) !==
                  deletedId
              );
          }
        }
      )

      .addCase(
        deletePanchayathThunk.rejected,
        (
          state,
          action
        ) => {
          state.deleting =
            false;

          state.error =
            action.payload ||
            "Failed to delete Panchayath";
        }
      );
  },
});

// ============================================================
// ACTION EXPORTS
// ============================================================

export const {
  setPmFilters,

  clearTaluks,

  setPmForm,

  resetPmForm,

  clearSelectedPanchayath,

  clearPanchayathError,
} = slice.actions;

// ============================================================
// REDUCER
// ============================================================

export default slice.reducer;